/**
 * /tim-kiem - corpus search page (M2 v2 Stage A, P-A5).
 *
 * Spec: specs/M2-EXECUTION-PLAN-v2.md §4 P-A5.
 * Phase 2 redesign (2026-05-22) Track B: Direction A "Tu phap restraint".
 *   - Replace orange accent voi navy + gold (mark highlight)
 *   - Display H1 -> Source Serif 4 (font-display)
 *   - Active filter chip -> navy (ask-chip--active)
 *   - Use Button + Input primitive
 *   - Snippet mark = gold-soft bg + gold-deep text
 *
 * v2 endpoint: GET /api/v1/search?q=&type=&year=&status=&org=&is_lw=&page=&limit=
 *
 * Layout (mobile-first 360px):
 *   - Hero serif H1 + sans subhead
 *   - Search Input primitive variant=search size=lg + Button "Tim kiem"
 *   - 5 filter row (chip style) - desktop; bottom-sheet drawer mobile
 *   - Result card stack render r.snippet HTML (FTS5 da inject <mark>)
 *   - Pagination 20/page max 50 page - Button primitive
 *   - Empty state voi 6 suggested keyword chip
 *   - Skeleton loader
 *   - URL state sync via React Router navigate
 *   - Mobile <720: bottom-sheet filter drawer
 *
 * Disclaimer block at end-of-page (CorpusDisclaimerBlock).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  searchCorpus,
  EFF_STATUS_LABEL_VI,
  SCOPE_LABEL_VI,
  type SearchHit,
  type SearchResponse,
  type SearchFilters,
  type ScopeCode,
} from '../lib/corpus-api';
import { AppShell } from '../components/shell/AppShell';
import { EffStatusBadge } from '../components/corpus/EffStatusBadge';
import { CorpusDisclaimerBlock } from '../components/corpus/CorpusDisclaimerBlock';
import { Button, Chip, Input } from '../components/primitives';
import { applyCorpusSearchSeo } from '../lib/seo-corpus-meta';

const MAX_PAGE = 50;

const DOC_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Tất cả loại văn bản' },
  { value: 'bo_luat', label: 'Bộ luật' },
  { value: 'luat', label: 'Luật' },
  { value: 'nghi_dinh', label: 'Nghị định' },
  { value: 'thong_tu', label: 'Thông tư' },
  { value: 'quyet_dinh', label: 'Quyết định' },
  { value: 'nghi_quyet', label: 'Nghị quyết' },
  { value: 'chi_thi', label: 'Chỉ thị' },
];

const EFF_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Tất cả tình trạng' },
  { value: 'CHL', label: EFF_STATUS_LABEL_VI.CHL },
  { value: 'HHL', label: EFF_STATUS_LABEL_VI.HHL },
  { value: 'HHL1P', label: EFF_STATUS_LABEL_VI.HHL1P },
  { value: 'TNHL', label: EFF_STATUS_LABEL_VI.TNHL },
];

const SCOPE_OPTIONS: Array<{ value: 'ALL' | ScopeCode; label: string }> = [
  { value: 'ALL', label: 'Tất cả phạm vi' },
  { value: 'TW', label: 'Trung ương' },
  { value: 'DP', label: 'Địa phương' },
];

const SUGGESTED_QUERIES = [
  'Bộ luật Lao động',
  'Luật Đất đai',
  'Hôn nhân gia đình',
  'Doanh nghiệp',
  'Thuế thu nhập',
  'An sinh xã hội',
];

const CURRENT_YEAR = new Date().getFullYear();

/**
 * URL -> SearchFilters reader.
 *
 * History:
 *   - 2026-05-27 SB3-05 introduced default `eff_status='CHL'` when URL had no
 *     `status=` param (with auto-disable carve-out for exact docNum queries).
 *   - 2026-05-28 HOTFIX (this file): SB3-05 default REVERTED. Rationale below.
 *
 * Why no default eff_status filter (per s2026-05-28-SB3-05-HOTFIX-HHL1P):
 *   1. SB3-05 default `CHL` hard-filtered out core laws marked HHL1P (Het
 *      hieu luc 1 phan = partially repealed but STILL PARTIALLY IN FORCE).
 *      Confirmed UAT failure 2026-05-28: query "luat thuong mai" returned
 *      VBHN circular at rank #1 instead of Luat Thuong mai 36/2005/QH11
 *      (HHL1P) which IS the source authority. Same regression on s02
 *      (BLLD 45/2019 HHL1P) + s09 (sa thai trai phap luat -> BLLD).
 *   2. Worker-side ranking already biases CHL via EFF_STATUS_BOOST_SQL
 *      (CHL +2.0 / HHL1P +1.5 / HHL 0.0 in src/workers/api/corpus-browser-m2/fts5.ts).
 *      Soft preference is sufficient; hard filter is redundant + harmful for HHL1P.
 *   3. vbpl.gov.vn (baseline) surfaces HHL1P laws regardless of status filter;
 *      they prefer "still partially in force" over guiding circulars (founder
 *      screenshot 2026-05-27).
 *   4. User retains explicit dropdown control. URL `?status=CHL` still hard
 *      filters when user explicitly chose it (URL state source of truth).
 *
 * Behavior post-hotfix:
 *   - eff_status taken VERBATIM from URL (empty default if no param).
 *   - No regex carve-out needed (no default to disable).
 *
 * Stale-URL cleanup (2026-05-28 follow-up):
 *   Tabs opened during SB3-05 window (2026-05-27 to 2026-05-28) have URL
 *   `?status=CHL` baked in from auto-default. Hotfix removed default but
 *   URL stickiness keeps surfacing CHL filter. Detect "user did not intentionally
 *   set CHL" via marker `?_fs=1` (filter-set flag written when user explicitly
 *   toggles status dropdown) - absent marker + `status=CHL` only filter means
 *   stale leftover, strip silently.
 */
const FILTER_SET_FLAG = '_fs';

function readFiltersFromUrl(search: string): SearchFilters {
  const sp = new URLSearchParams(search);
  const q = sp.get('q') ?? '';
  let eff_status = sp.get('status') ?? sp.get('eff_status') ?? '';
  // Stale URL strip: if `status=CHL` is the ONLY active filter and no
  // `_fs=1` marker (user-set indicator), treat as leftover from SB3-05 era.
  const hasOtherFilter =
    Boolean(sp.get('type') ?? sp.get('doc_type')) ||
    Boolean(sp.get('org')) ||
    Boolean(sp.get('year')) ||
    Boolean(sp.get('scope') && sp.get('scope') !== 'ALL') ||
    sp.has('is_lw');
  const userMarkedFilter = sp.get(FILTER_SET_FLAG) === '1';
  if (eff_status === 'CHL' && !userMarkedFilter && !hasOtherFilter) {
    eff_status = '';
  }
  const filters: SearchFilters = {
    q,
    doc_type: sp.get('type') ?? sp.get('doc_type') ?? '',
    eff_status,
    org: sp.get('org') ?? '',
    scope: (sp.get('scope') as ScopeCode | null) ?? 'ALL',
    page: sp.get('page') ? Math.max(1, Math.min(MAX_PAGE, Number(sp.get('page')))) : 1,
  };
  const year = sp.get('year');
  if (year && /^\d{4}$/.test(year)) filters.year = year;
  const isLw = sp.get('is_lw');
  if (isLw === '0') filters.is_lw = 0;
  else if (isLw === '1') filters.is_lw = 1;
  return filters;
}

function filtersToQs(f: SearchFilters): string {
  const qs = new URLSearchParams();
  if (f.q) qs.set('q', f.q);
  if (f.doc_type) qs.set('type', f.doc_type);
  if (f.eff_status) {
    qs.set('status', f.eff_status);
    // Mark "user-set" so stale-URL strip in readFiltersFromUrl preserves this
    // when status equals CHL (otherwise it would treat as SB3-05 leftover).
    qs.set(FILTER_SET_FLAG, '1');
  }
  if (f.year) qs.set('year', f.year);
  if (f.org) qs.set('org', f.org);
  if (f.is_lw === 0 || f.is_lw === 1) qs.set('is_lw', String(f.is_lw));
  if (f.scope && f.scope !== 'ALL') qs.set('scope', f.scope);
  if (f.page && f.page > 1) qs.set('page', String(f.page));
  return qs.toString();
}

export function CorpusSearchRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<SearchFilters>(() => readFiltersFromUrl(location.search));
  const [response, setResponse] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const requestSeqRef = useRef(0);

  useEffect(() => {
    applyCorpusSearchSeo({
      query: filters.q,
      total: response?.total,
    });
  }, [filters.q, response?.total]);

  // URL -> state sync (read once on nav).
  useEffect(() => {
    setFilters(readFiltersFromUrl(location.search));
  }, [location.search]);

  // Fetch whenever filters change (skip if q empty - server returns 400).
  useEffect(() => {
    const q = (filters.q ?? '').trim();
    if (!q) {
      setResponse(null);
      setLoading(false);
      setError(null);
      return;
    }
    // 2026-05-28 SB3-05 HOTFIX: no eff_status default + no carve-out needed.
    // URL state source of truth for filter. Worker-side EFF_STATUS_BOOST_SQL
    // handles CHL/HHL1P/HHL ranking preference (see fts5.ts EFF_STATUS_BOOST_SQL).
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);
    searchCorpus(filters)
      .then((data) => {
        if (seq !== requestSeqRef.current) return;
        setResponse(data);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (seq !== requestSeqRef.current) return;
        setError(err.message || 'unknown');
        setLoading(false);
      });
  }, [filters]);

  const updateFilters = useCallback(
    (patch: Partial<SearchFilters>, opts?: { resetPage?: boolean }) => {
      setFilters((prev) => {
        const next: SearchFilters = {
          ...prev,
          ...patch,
          page: opts?.resetPage ? 1 : (patch.page ?? prev.page ?? 1),
        };
        const qs = filtersToQs(next);
        navigate(`/tim-kiem${qs ? `?${qs}` : ''}`, { replace: false });
        return next;
      });
    },
    [navigate],
  );

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      updateFilters({}, { resetPage: true });
    },
    [updateFilters],
  );

  const totalPages = response
    ? Math.min(MAX_PAGE, Math.max(1, Math.ceil(response.total / response.per_page)))
    : 1;

  const hasActiveFilter =
    Boolean(filters.doc_type) ||
    Boolean(filters.eff_status) ||
    Boolean(filters.org) ||
    Boolean(filters.year) ||
    (filters.scope !== undefined && filters.scope !== 'ALL');

  const q = (filters.q ?? '').trim();

  return (
    <AppShell variant="flow">
    {/* AppShell rail-only Phase 1 (spec 2026-06-12): rail trai + content hien
        tai gian giua. Master-detail (cot ket qua + khung doc) lam o Phase 2. */}
    <main
      style={{
        maxWidth: 1100,
        margin: '0 auto',
        padding: '40px 16px 56px',
        fontFamily: 'var(--font-sans)',
        width: '100%',
      }}
    >
      <header style={{ marginBottom: 24 }}>
        <p
          className="eyebrow"
          style={{
            margin: '0 0 8px',
            color: 'var(--asklaw-navy)',
            letterSpacing: '0.18em',
          }}
        >
          Tư liệu pháp luật
        </p>
        <h1
          style={{
            margin: '0 0 12px',
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            lineHeight: 1.15,
            fontSize: 'clamp(28px, 4vw, 44px)',
            color: 'var(--asklaw-navy)',
          }}
        >
          Tìm văn bản pháp luật
        </h1>
        <p
          style={{
            margin: 0,
            color: 'var(--asklaw-ink-soft)',
            fontSize: 16,
            lineHeight: 1.55,
            maxWidth: 720,
          }}
        >
          Tra cứu kho văn bản pháp luật Việt Nam với trích dẫn Điều / Khoản
          đầy đủ.
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        role="search"
        aria-label="Tìm kiếm văn bản"
        style={{
          marginBottom: 16,
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: '1 1 320px', minWidth: 220 }}>
          <Input
            variant="search"
            inputSize="lg"
            type="search"
            value={filters.q ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Nhập từ khoá, số văn bản, hoặc chủ đề pháp luật..."
            aria-label="Từ khoá tìm kiếm"
          />
        </div>
        <Button type="submit" variant="primary" size="lg">
          Tìm
        </Button>
        {filters.q ? (
          <Button
            type="button"
            variant="ghost"
            size="lg"
            aria-label="Xoá từ khoá"
            onClick={() => updateFilters({ q: '' }, { resetPage: true })}
          >
            Xoá
          </Button>
        ) : null}
      </form>

      <FilterRow
        filters={filters}
        onChange={(patch) => updateFilters(patch, { resetPage: true })}
        onOpenDrawer={() => setDrawerOpen(true)}
        hasActive={hasActiveFilter}
      />

      {drawerOpen ? (
        <FilterDrawer
          filters={filters}
          onClose={() => setDrawerOpen(false)}
          onApply={(patch) => {
            updateFilters(patch, { resetPage: true });
            setDrawerOpen(false);
          }}
          onReset={() => {
            updateFilters(
              {
                doc_type: '',
                eff_status: '',
                org: '',
                scope: 'ALL',
                year: undefined,
              },
              { resetPage: true },
            );
          }}
        />
      ) : null}

      {/* Result count line */}
      {response && q ? (
        <p
          aria-live="polite"
          style={{
            margin: '20px 0 12px',
            color: 'var(--asklaw-muted)',
            fontSize: 13.5,
          }}
        >
          {response.total === 0
            ? 'Không có kết quả phù hợp'
            : `Tìm thấy ${response.total.toLocaleString('vi-VN')} văn bản`}{' '}
          cho từ khoá <strong style={{ color: 'var(--asklaw-ink)' }}>"{q}"</strong>
          {response.cache_hit ? (
            <span
              style={{ marginLeft: 8, fontSize: 11.5, opacity: 0.7, cursor: 'help' }}
              title="Kết quả được lưu tạm để hiển thị nhanh hơn"
              aria-label="Kết quả được lưu tạm để hiển thị nhanh hơn"
            >
              (lưu tạm)
            </span>
          ) : null}
        </p>
      ) : null}

      {/* Result body */}
      {!q ? (
        <InitialPrompt onSuggest={(query) => updateFilters({ q: query }, { resetPage: true })} />
      ) : loading ? (
        <SkeletonList />
      ) : error ? (
        <div
          role="alert"
          style={{
            padding: 20,
            border: '1px solid var(--asklaw-red-soft)',
            background: 'var(--asklaw-red-soft)',
            borderLeft: '4px solid var(--asklaw-red)',
            borderRadius: 12,
            color: 'var(--asklaw-red-deep)',
          }}
        >
          Đã có lỗi xảy ra khi tải kết quả. Vui lòng thử lại sau ít phút.
        </div>
      ) : response && response.results.length > 0 ? (
        <ResultList results={response.results} />
      ) : (
        <EmptyState
          onSuggest={(query) => updateFilters({ q: query }, { resetPage: true })}
        />
      )}

      {response && response.total > 0 ? (
        <Pagination
          page={response.page}
          totalPages={totalPages}
          onChange={(page) => {
            updateFilters({ page });
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      ) : null}

      <CorpusDisclaimerBlock />
    </main>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FilterRow({
  filters,
  onChange,
  onOpenDrawer,
  hasActive,
}: {
  filters: SearchFilters;
  onChange: (patch: Partial<SearchFilters>) => void;
  onOpenDrawer: () => void;
  hasActive: boolean;
}) {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 720px)');
    const handler = () => setIsMobile(mql.matches);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  if (isMobile) {
    return (
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
        <Chip
          variant={hasActive ? 'active' : 'default'}
          size="md"
          onClick={onOpenDrawer}
          aria-pressed={hasActive}
        >
          Bộ lọc {hasActive ? '(đang áp dụng)' : ''}
        </Chip>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        marginTop: 4,
      }}
    >
      <SelectChip
        label="Loại văn bản"
        value={filters.doc_type ?? ''}
        options={DOC_TYPE_OPTIONS}
        onChange={(value) => onChange({ doc_type: value })}
      />
      <SelectChip
        label="Tình trạng hiệu lực"
        value={filters.eff_status ?? ''}
        options={EFF_STATUS_OPTIONS}
        onChange={(value) => onChange({ eff_status: value })}
      />
      <YearChip
        year={filters.year}
        onChange={(year) => onChange({ year })}
      />
      <AgencyChip
        value={filters.org ?? ''}
        onChange={(value) => onChange({ org: value })}
      />
      <SelectChip
        label="Phạm vi"
        value={(filters.scope as string) ?? 'ALL'}
        options={SCOPE_OPTIONS as Array<{ value: string; label: string }>}
        onChange={(value) => onChange({ scope: value as 'ALL' | ScopeCode })}
      />
    </div>
  );
}

function filterShellStyle(active: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    borderRadius: 9999,
    border: active
      ? '1px solid var(--asklaw-navy)'
      : '1px solid var(--asklaw-border-strong)',
    background: active ? 'var(--asklaw-navy)' : 'var(--asklaw-paper)',
    color: active ? 'var(--asklaw-paper)' : 'var(--asklaw-ink)',
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: 'var(--font-sans)',
    fontWeight: 500,
    minHeight: 36,
    transition: 'background 120ms ease, border-color 120ms ease, color 120ms ease',
  };
}

function SelectChip({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  const active = value !== '' && value !== 'ALL';
  return (
    <label style={filterShellStyle(active)}>
      <span style={{ color: active ? 'var(--asklaw-navy-soft)' : 'var(--asklaw-muted)' }}>
        {label}:
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        style={{
          border: 0,
          background: 'transparent',
          color: 'inherit',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          outline: 'none',
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} style={{ color: 'var(--asklaw-ink)' }}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function YearChip({
  year,
  onChange,
}: {
  year: string | undefined;
  onChange: (year: string | undefined) => void;
}) {
  const active = Boolean(year);
  return (
    <label style={filterShellStyle(active)}>
      <span style={{ color: active ? 'var(--asklaw-navy-soft)' : 'var(--asklaw-muted)' }}>
        Năm ban hành:
      </span>
      <input
        type="number"
        min={1945}
        max={CURRENT_YEAR}
        value={year ?? ''}
        onChange={(e) => {
          const v = e.target.value.trim();
          if (!v) onChange(undefined);
          else if (/^\d{4}$/.test(v)) onChange(v);
          else onChange(undefined);
        }}
        placeholder="vd 2020"
        aria-label="Năm ban hành"
        style={{
          width: 80,
          border: 0,
          background: 'transparent',
          color: 'inherit',
          fontFamily: 'var(--font-mono)',
          fontSize: 13,
          fontWeight: 600,
          outline: 'none',
          padding: '4px 0',
        }}
      />
    </label>
  );
}

function AgencyChip({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const active = value !== '';
  return (
    <label style={filterShellStyle(active)}>
      <span style={{ color: active ? 'var(--asklaw-navy-soft)' : 'var(--asklaw-muted)' }}>
        Cơ quan:
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="vd Quốc hội"
        aria-label="Cơ quan ban hành"
        style={{
          minWidth: 140,
          border: 0,
          background: 'transparent',
          color: 'inherit',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 600,
          outline: 'none',
          padding: '4px 0',
        }}
      />
    </label>
  );
}

function FilterDrawer({
  filters,
  onClose,
  onApply,
  onReset,
}: {
  filters: SearchFilters;
  onClose: () => void;
  onApply: (patch: Partial<SearchFilters>) => void;
  onReset: () => void;
}) {
  const [draft, setDraft] = useState<SearchFilters>(filters);
  return (
    <div
      role="dialog"
      aria-label="Bộ lọc văn bản"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(12, 18, 32, 0.45)',
        display: 'flex',
        alignItems: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          background: 'var(--asklaw-paper-elev)',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: '20px 20px 28px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2
            style={{
              margin: 0,
              fontSize: 18,
              color: 'var(--asklaw-navy)',
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
            }}
          >
            Bộ lọc
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={onClose}
            aria-label="Đóng bộ lọc"
          >
            Đóng
          </Button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
          <DrawerSelect
            label="Loại văn bản"
            value={draft.doc_type ?? ''}
            options={DOC_TYPE_OPTIONS}
            onChange={(v) => setDraft((d) => ({ ...d, doc_type: v }))}
          />
          <DrawerSelect
            label="Tình trạng hiệu lực"
            value={draft.eff_status ?? ''}
            options={EFF_STATUS_OPTIONS}
            onChange={(v) => setDraft((d) => ({ ...d, eff_status: v }))}
          />
          <DrawerSelect
            label="Phạm vi"
            value={(draft.scope as string) ?? 'ALL'}
            options={SCOPE_OPTIONS as Array<{ value: string; label: string }>}
            onChange={(v) => setDraft((d) => ({ ...d, scope: v as 'ALL' | ScopeCode }))}
          />
          <label style={{ fontSize: 13, color: 'var(--asklaw-muted)' }}>
            Năm ban hành
            <input
              type="number"
              min={1945}
              max={CURRENT_YEAR}
              value={draft.year ?? ''}
              onChange={(e) => {
                const v = e.target.value.trim();
                setDraft((d) => ({ ...d, year: /^\d{4}$/.test(v) ? v : undefined }));
              }}
              placeholder="vd 2020"
              style={drawerInputStyle}
            />
          </label>
          <label style={{ fontSize: 13, color: 'var(--asklaw-muted)' }}>
            Cơ quan ban hành
            <input
              type="text"
              value={draft.org ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, org: e.target.value }))}
              placeholder="vd Quốc hội, Chính phủ..."
              style={drawerInputStyle}
            />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={onReset}
            style={{ flex: 1 }}
          >
            Đặt lại
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={() => onApply(draft)}
            style={{ flex: 1 }}
          >
            Áp dụng
          </Button>
        </div>
      </div>
    </div>
  );
}

const drawerInputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 6,
  padding: '10px 12px',
  border: '1px solid var(--asklaw-border)',
  borderRadius: 8,
  background: 'var(--asklaw-paper)',
  color: 'var(--asklaw-ink)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

function DrawerSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  return (
    <label style={{ fontSize: 13, color: 'var(--asklaw-muted)' }}>
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} style={drawerInputStyle}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ResultList({ results }: { results: SearchHit[] }) {
  return (
    <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 14 }}>
      {results.map((r) => (
        <li key={r.doc_id}>
          <Link
            to={`/van-ban/${encodeURIComponent(r.doc_id)}`}
            className="corpus-result-card"
            style={{
              display: 'block',
              padding: '18px 20px',
              background: 'var(--asklaw-paper-elev)',
              border: '1px solid var(--asklaw-border)',
              borderLeft: '3px solid var(--asklaw-border)',
              borderRadius: 12,
              textDecoration: 'none',
              color: 'var(--asklaw-ink)',
              transition:
                'box-shadow 140ms ease, border-color 140ms ease, transform 140ms ease',
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLAnchorElement;
              el.style.boxShadow = 'var(--shadow-md)';
              el.style.borderLeftColor = 'var(--asklaw-navy)';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLAnchorElement;
              el.style.boxShadow = 'var(--shadow-sm)';
              el.style.borderLeftColor = 'var(--asklaw-border)';
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: 17,
                  fontWeight: 600,
                  lineHeight: 1.35,
                  flex: 1,
                  minWidth: 0,
                  fontFamily: 'var(--font-display)',
                  color: 'var(--asklaw-navy)',
                  letterSpacing: '-0.005em',
                }}
              >
                {r.title}
              </h2>
              <EffStatusBadge status={r.eff_status_code} />
            </div>
            <div
              style={{
                display: 'flex',
                gap: 14,
                flexWrap: 'wrap',
                marginTop: 8,
                color: 'var(--asklaw-muted)',
                fontSize: 13,
              }}
            >
              {r.doc_num ? (
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--asklaw-navy-deep)',
                    fontWeight: 500,
                  }}
                >
                  {r.doc_num}
                </span>
              ) : null}
              {r.agency_name ? <span>{r.agency_name}</span> : null}
              {r.issue_date ? <span>{formatDateVi(r.issue_date)}</span> : null}
              <span style={{ marginLeft: 'auto' }}>
                {r.scope === 'TW' ? SCOPE_LABEL_VI.TW : SCOPE_LABEL_VI.DP}
              </span>
            </div>
            {r.snippet ? (
              <p
                style={{
                  margin: '10px 0 0',
                  color: 'var(--asklaw-ink-soft)',
                  fontSize: 14.5,
                  lineHeight: 1.6,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
                dangerouslySetInnerHTML={{ __html: renderSnippet(r.snippet) }}
              />
            ) : null}
          </Link>
        </li>
      ))}
    </ol>
  );
}

function SkeletonList() {
  return (
    <ul
      aria-busy
      aria-label="Đang tải kết quả"
      style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 14 }}
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <li
          key={i}
          style={{
            padding: 20,
            background: 'var(--asklaw-paper-elev)',
            border: '1px solid var(--asklaw-border)',
            borderRadius: 12,
          }}
        >
          <Shimmer width="60%" height={18} />
          <div style={{ marginTop: 10 }}>
            <Shimmer width="40%" height={12} />
          </div>
          <div style={{ marginTop: 16 }}>
            <Shimmer width="95%" height={12} />
          </div>
          <div style={{ marginTop: 6 }}>
            <Shimmer width="85%" height={12} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function Shimmer({ width, height }: { width: number | string; height: number }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'block',
        width,
        height,
        borderRadius: 6,
        background:
          'linear-gradient(90deg, var(--asklaw-paper-sunk) 0%, var(--asklaw-border) 50%, var(--asklaw-paper-sunk) 100%)',
        backgroundSize: '200% 100%',
        animation: 'corpus-shimmer 1.4s ease-in-out infinite',
      }}
    />
  );
}

function InitialPrompt({ onSuggest }: { onSuggest: (q: string) => void }) {
  return (
    <div
      style={{
        padding: '36px 24px',
        textAlign: 'center',
        background: 'var(--asklaw-paper-elev)',
        border: '1px dashed var(--asklaw-border-strong)',
        borderRadius: 14,
      }}
    >
      <h2
        style={{
          margin: '0 0 8px',
          fontSize: 20,
          color: 'var(--asklaw-navy)',
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
        }}
      >
        Bắt đầu tra cứu
      </h2>
      <p style={{ margin: 0, color: 'var(--asklaw-muted)', fontSize: 14 }}>
        Nhập từ khoá vào ô tìm kiếm phía trên hoặc chọn một gợi ý bên dưới.
      </p>
      <div
        style={{
          marginTop: 18,
          display: 'flex',
          gap: 8,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}
      >
        {SUGGESTED_QUERIES.map((qq) => (
          <Chip
            key={qq}
            variant="default"
            size="md"
            onClick={() => onSuggest(qq)}
          >
            {qq}
          </Chip>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ onSuggest }: { onSuggest: (q: string) => void }) {
  return (
    <div
      style={{
        padding: '48px 24px',
        textAlign: 'center',
        background: 'var(--asklaw-paper-elev)',
        border: '1px dashed var(--asklaw-border-strong)',
        borderRadius: 14,
      }}
    >
      <svg
        width="48"
        height="48"
        viewBox="0 0 48 48"
        aria-hidden
        style={{ color: 'var(--asklaw-navy)', opacity: 0.85 }}
      >
        <circle cx="20" cy="20" r="13" stroke="currentColor" strokeWidth="2.5" fill="none" />
        <path d="M30 30l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
      <h2
        style={{
          margin: '14px 0 6px',
          fontSize: 20,
          color: 'var(--asklaw-navy)',
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
        }}
      >
        Không tìm thấy kết quả
      </h2>
      <p style={{ margin: 0, color: 'var(--asklaw-ink-soft)', fontSize: 14.5, lineHeight: 1.55 }}>
        Thử từ khoá khác, hoặc duyệt theo chủ đề pháp luật bên dưới.
      </p>
      <div
        style={{
          marginTop: 18,
          display: 'flex',
          gap: 8,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}
      >
        {SUGGESTED_QUERIES.map((qq) => (
          <Chip
            key={qq}
            variant="default"
            size="md"
            onClick={() => onSuggest(qq)}
          >
            {qq}
          </Chip>
        ))}
      </div>
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <nav
      aria-label="Trang kết quả"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        marginTop: 28,
        flexWrap: 'wrap',
      }}
    >
      <Button
        type="button"
        variant="secondary"
        size="md"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
      >
        Trước
      </Button>
      <span
        aria-live="polite"
        style={{
          color: 'var(--asklaw-muted)',
          fontSize: 14,
          fontFamily: 'var(--font-mono)',
          padding: '0 12px',
        }}
      >
        Trang {page} / {totalPages}
      </span>
      <Button
        type="button"
        variant="secondary"
        size="md"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
      >
        Sau
      </Button>
      {totalPages === MAX_PAGE ? (
        <span style={{ color: 'var(--asklaw-muted)', fontSize: 12, marginLeft: 12 }}>
          Hãy thu hẹp bộ lọc để kết quả chính xác hơn.
        </span>
      ) : null}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

/**
 * Render snippet from FTS5. Server returns text fragment with <mark> markers
 * already injected; we keep <mark> tag, strip all other tags (safe-list).
 *
 * Phase 2 (2026-05-22) Direction A: mark = gold-soft bg + gold-deep text.
 * Util class `corpus-mark` defined in global.css.
 */
function renderSnippet(raw: string): string {
  // Strip all tag except <mark> and </mark>; entity-escape stray brackets.
  // FTS5 highlight() output is plain text + balanced <mark>...</mark> pair.
  const text = String(raw ?? '');
  // Escape & < > " ' to entities, then re-allow <mark> tag only.
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
  return escaped
    .replace(/&lt;mark&gt;/g, '<mark class="corpus-mark">')
    .replace(/&lt;\/mark&gt;/g, '</mark>');
}

function formatDateVi(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}
